import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import type { Application, ApplicationStatus } from "@shared/types";

const statusColors: Record<ApplicationStatus, string> = {
  new: "bg-blue-500",
  reviewing: "bg-yellow-500",
  interview: "bg-purple-500",
  hired: "bg-green-500",
  rejected: "bg-red-500",
};

export default function AdminApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<ApplicationStatus>("new");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (id) {
      loadApplication(parseInt(id));
    }
  }, [id]);

  const loadApplication = async (appId: number) => {
    try {
      const response = await apiClient.admin.applications.get(appId);
      const app = response.data as Application;
      setApplication(app);
      setStatus(app.status);
      setNotes(app.notes || "");
    } catch (error) {
      console.error("Failed to load application:", error);
      toast.error("Failed to load application");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    try {
      await apiClient.admin.applications.update(parseInt(id), { status, notes });
      toast.success("Application updated");
      setApplication((prev) => (prev ? { ...prev, status, notes } : null));
    } catch (error) {
      console.error("Failed to update application:", error);
      toast.error("Failed to update application");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Application not found</p>
        <Button onClick={() => navigate("/admin/applications")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/admin/applications")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">
            {application.firstName} {application.lastName}
          </h1>
          <p className="text-muted-foreground">
            Applied on {formatDate(application.createdAt)}
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">
                  {application.firstName} {application.lastName}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{application.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Phone</p>
                <p className="font-medium">{application.phone}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">
                  {[application.city, application.state]
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Professional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">CDL Class</p>
                <p className="font-medium">{application.cdlClass || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Years Experience</p>
                <p className="font-medium">
                  {application.yearsExperience ?? "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CDL Number</p>
                <p className="font-medium">{application.cdlNumber || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CDL State</p>
                <p className="font-medium">{application.cdlState || "N/A"}</p>
              </div>
            </div>
            {application.endorsements && application.endorsements.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Endorsements</p>
                <div className="flex flex-wrap gap-2">
                  {application.endorsements.map((e) => (
                    <Badge key={e} variant="secondary">
                      {e}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Accidents/Violations
              </p>
              <p className="font-medium">
                {application.accidentsViolations || "None reported"}
              </p>
            </div>
            <div className="flex gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Drug Test Consent</p>
                <Badge variant={application.drugTestConsent ? "default" : "secondary"}>
                  {application.drugTestConsent ? "Yes" : "No"}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Background Check Consent
                </p>
                <Badge
                  variant={
                    application.backgroundCheckConsent ? "default" : "secondary"
                  }
                >
                  {application.backgroundCheckConsent ? "Yes" : "No"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status & Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Status & Notes
              <Badge className={`${statusColors[application.status]} text-white`}>
                {application.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Update Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as ApplicationStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewing">Reviewing</SelectItem>
                  <SelectItem value="interview">Interview</SelectItem>
                  <SelectItem value="hired">Hired</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this application..."
                rows={4}
              />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
